import { ApiError, CancelablePromise, CancelError } from 'api-clients/monolith';
import { useCallback, useEffect, useRef, useState } from 'react';

import {
  buildErrorState,
  buildLoadingState,
  buildReadyState,
  buildreloadState,
  ReloadableResultStatus,
} from '../api/resultStatus';

export interface UseApiService<T, E> {
  result: ReloadableResultStatus<T, E>;
  /**
   * Triggers a refetch and returns a promise that resolves once `result`
   * reflects a request from this call or later (success or error -- a
   * request that gets canceled/superseded by a subsequent refetch does not
   * resolve it). Callers that need to act on fresh data -- e.g. navigating
   * to a URL keyed off a resource this fetch will include -- should `await`
   * it instead of assuming `result` is up to date immediately after calling
   * `refetch()`. A disabled hook makes no request, so its `refetch()`
   * resolves at once and `result` stays as it was.
   */
  refetch: () => Promise<void>;
  cancel: () => void;
}

export interface UseApiServiceOptions {
  /** When false, the request is not made. Default true. */
  enabled?: boolean;
}

/**
 * Should be kept in sync with the copy of `applicant-ui`'s `useApiService`
 * hook to allow both to reference a single `:packages/react-hooks` or similar
 *
 * @see {@link :packages/applicant-ui/app/hooks/useApiService.ts}
 */
export const useApiService = <T, E = unknown>(
  // This service should be memoized before passed in, for example by using `useCallback`.
  service: () => CancelablePromise<T>,
  options?: UseApiServiceOptions,
): UseApiService<T, E> => {
  const enabled = options?.enabled !== false;
  const [result, setResult] =
    useState<ReloadableResultStatus<T, E>>(buildLoadingState());
  // A monotonically increasing counter, not a boolean toggle: two refetch()
  // calls in the same React 18 batch would flip a boolean back to its
  // original value (false -> true -> false), and React bails out of
  // re-rendering when a state update computes back to the current value --
  // no effect run, so neither call's promise would ever resolve. Every call
  // to setRefetchTick's updater produces a value distinct from the starting
  // one, so each refetch() always schedules a fetch.
  const [refetchTick, setRefetchTick] = useState(0);
  const promiseRef = useRef<CancelablePromise<T> | null>(null);
  // Each fetch attempt (initial load or refetch) is tagged with an
  // incrementing generation. A waiter is resolved only once a request from
  // its own generation or later actually settles with real data (success or
  // error) -- a canceled/superseded generation leaves its waiters queued
  // rather than resolving them, since `result` was never updated to match.
  // Without this, an earlier request's cancellation could resolve a waiter
  // for a later refetch that's still in flight, handing the caller stale
  // `result` data.
  const generationRef = useRef(0);
  const pendingRefetchResolversRef = useRef<
    Array<{ resolve: () => void; targetGeneration: number }>
  >([]);

  const setLoadingOrReloading = useCallback(() => {
    setResult(currentResult => {
      // Has loaded once succesfully and has valid cached data
      if (currentResult.status === 'ready') {
        return buildreloadState<T>(currentResult.data);
      }
      if (currentResult.status !== 'loading') {
        return buildLoadingState();
      }
      return currentResult;
    });
  }, []);

  const cancel = useCallback(() => {
    if (promiseRef.current) {
      promiseRef.current.cancel();
      promiseRef.current = null;
    }
  }, []);

  const refetch = useCallback(() => {
    // No effect run fetches while disabled, so no generation would ever
    // settle a waiter queued here.
    if (!enabled) {
      return Promise.resolve();
    }
    return new Promise<void>(resolve => {
      // The upcoming effect run (triggered by the refetch tick bump below)
      // will claim the next generation -- bind this waiter to it so it
      // can't be resolved by a request that was already in flight before
      // this call.
      pendingRefetchResolversRef.current.push({
        resolve,
        targetGeneration: generationRef.current + 1,
      });
      setRefetchTick(currentTick => currentTick + 1);
    });
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      // Disabling cancels the in-flight request, and nothing fetches until
      // the hook is enabled again, so release every waiter now.
      pendingRefetchResolversRef.current.forEach(waiter => waiter.resolve());
      pendingRefetchResolversRef.current = [];
      return () => {};
    }
    setLoadingOrReloading();
    generationRef.current += 1;
    const myGeneration = generationRef.current;
    const promise = service();
    promiseRef.current = promise;

    const resolveWaitersUpTo = (generation: number) => {
      const stillPending: typeof pendingRefetchResolversRef.current = [];
      pendingRefetchResolversRef.current.forEach(waiter => {
        if (waiter.targetGeneration <= generation) {
          waiter.resolve();
        } else {
          stillPending.push(waiter);
        }
      });
      pendingRefetchResolversRef.current = stillPending;
    };

    promise
      .then(data => {
        setResult(buildReadyState(data));
        promiseRef.current = null;
        resolveWaitersUpTo(myGeneration);
      })
      .catch((e: Error) => {
        if (e instanceof CancelError) {
          // Superseded by a newer request -- leave any waiters queued for
          // whichever generation actually settles next.
          return;
        }
        if (e instanceof ApiError) {
          setResult(
            buildErrorState((e.body as E) || (e.statusText as unknown as E)),
          );
          promiseRef.current = null;
          resolveWaitersUpTo(myGeneration);
          return;
        }
        setResult(buildErrorState());
        promiseRef.current = null;
        resolveWaitersUpTo(myGeneration);
      });
    return () => promise.cancel();
  }, [enabled, service, refetchTick, setLoadingOrReloading]);

  return { result, refetch, cancel };
};
